import { useEffect, useState } from 'react';
import { Header } from '../../shared/components/Header';
import { Text } from '@gs-ux-uitoolkit-react/text';
import { Button } from '@gs-ux-uitoolkit-react/button';
import { Footer } from '../../shared/components/Footer';
import { FormControl } from '@gs-ux-uitoolkit-react/form';
import { FormInput } from '../../shared/components/FormInput';
import {useNavigate, useParams} from 'react-router-dom';
import axios from 'axios';
import { toastService } from '@gs-ux-uitoolkit-react/toast';
import { Checkbox } from "@gs-ux-uitoolkit-react/checkbox";
import { LegacyIcon } from "@gs-ux-uitoolkit-react/icon-font";

export function AdviseeSetPassword() {
    const navigate = useNavigate(); // Initialize the navigate function
    const { token } = useParams<{ token: string }>(); // Extract repId from the URL
    const [isPasswordCreated, setIsPasswordCreated] = useState(false);
    const [firmRepInfo, setFirmRepInfo] = useState<any>();
    const [password, setPassword] = useState('');
    const [confirmPassword, setConfirmPassword] = useState('');
    const [check, setCheck] = useState(false);
    const [passwordMasked, setPasswordMasked] = useState(true);
    const [confirmPasswordMasked, setConfirmPasswordMasked] = useState(true);
    const [isSetPassword, setIsSetPassword] = useState(false);
    const passwordsMatch = password === confirmPassword; // Check if passwords match
    const [linkDeactivatedError, setLinkDeactivatedError] = useState<string | null>(null);

    const getRedirectUrl = () => {
        const domain = window.location.hostname.toLowerCase();
        const isAdvisorSite = domain.includes('advisorsolutions')

        let redirectUrl = isAdvisorSite
            ? 'https://advisorsolutions.gs.com/app/'
            : 'https://custodysolutions.gs.com/client-app/';

        if (domain.includes('dev.')) {
            redirectUrl = isAdvisorSite
                ? 'https://dev.advisorsolutions.site.gs.com/app/'
                : 'https://dev.custodysolutions.site.gs.com/client-app/';
        } else if (domain.includes('qa.') || domain.includes('localhost') || domain.includes('127.0.0.1')) {
            redirectUrl = isAdvisorSite
                ? 'https://qa.advisorsolutions.site.gs.com/app/'
                : 'https://qa.custodysolutions.site.gs.com/client-app/';
        } else if (domain.includes('uat.')) {
            redirectUrl = isAdvisorSite
                ? 'https://uat.advisorsolutions.gs.com/app/'
                : 'https://uat.custodysolutions.gs.com/client-app/';
        }

        return redirectUrl;
    };

    useEffect(() => {
        const fetchData = async () => {
            if (token) {
                const url = `/api/v2/password-invitation/${token}/summary`;
                try {
                    const response = await axios.get(url);
                    if (response.data.isComplete === 'Y') {
                        setIsPasswordCreated(true);
                    }
                } catch (error: any) {
                    if (error?.response?.status === 401) {
                        navigate(`/create-password?token=${token}`);
                    } else if (error?.response?.data?.displayMessage) {
                        setLinkDeactivatedError(error.response.data.displayMessage);
                    }
                }
            }
        };

        fetchData();
    }, [token, navigate]);

    const validatePassword = (password: string): string | null => {
        if (password.length < 10 || password.length > 64) {
            return "Password must be between 10-64 characters long.";
        }
        if (!/[A-Z]/.test(password)) {
            return "Password must contain at least one upper-case letter.";
        }
        if (!/[a-z]/.test(password)) {
            return "Password must contain at least one lower-case letter.";
        }
        if (!/[0-9!@#$%^&*(),.?":{}|<>]/.test(password)) {
            return "Password must contain at least one number or symbol.";
        }
        return null; // No errors
    };

    const handleSubmit = () => {
        const errors: string[] = [];

        const passwordError = validatePassword(password);
        if (passwordError) {
            errors.push(passwordError);
        }

        if (!password || !confirmPassword || !passwordsMatch || !check) {
            errors.push('Please fill in all fields!');
        }

        if (errors.length > 0) {
            errors.forEach((error, index) => {
                setTimeout(() => {
                    toastService.show({
                        status: 'error',
                        message: error,
                        placement: 'top',
                        dismissible: true,
                        className: 'top-20',
                        autoDismiss: 3_500
                    });
                }, index * 100); // Slight delay between each toast
            });
            return;
        }
        const url = `/api/v2/password-invitation/${token}/set-password-and-secret-question-answer`;
        const advisorRequest = {
            "setPasswordRequest": {
                "newPassword": password
            },
            "updateCustomerAgreementRequest": {
                "isMemberAgreement": check ? 'Y' : 'N',
            }
        };

        axios.post(url, advisorRequest)
            .then(async (response) => {
                console.log(response);
                setIsSetPassword(true);
                setIsPasswordCreated(true);
            })
            .catch((error) => {
                console.log(error);
                toastService.show({
                    status: 'error',
                    message: `Failed to create Password!`,
                    placement: 'top',
                    dismissible: true,
                    className: 'top-20',
                    autoDismiss: 3_500
                });
            });
    };

    return (
        <>
            <Header />
            {linkDeactivatedError === "Your account is locked due to too many failed attempts. Please contact support." ? (
                <div className="bg-red-10 p-4 rounded w-full max-w-screen-md mx-auto mt-4">
                    <Text typography="textBody02Medium" color="red060" className="mb-2">
                        Error
                    </Text>
                    <Text typography="textBody02Regular" color="red060">
                        {linkDeactivatedError}
                    </Text>
                </div>
            ) : (
                <>
                    <div className="justify-between flex-1 flex flex-col min-[700px]:flex-row items-center px-16 py-6 xl:gap-72 lg:gap-24 md:gap-12 sm:gap-6">
                        <div className="self-stretch px-6 inline-flex justify-center items-center gap-2.5">
                        </div>
                    </div>
                    {isPasswordCreated ? (
                        <div className="flex-1 flex flex-col px-16 min-h-[400px]">
                            <Text typography="textBody01Regular" className='px-6'>
                                Your password has been created. Please proceed to{" "}
                                <a
                                    onClick={() => {
                                        window.open(getRedirectUrl(), '_blank');
                                    }}
                                    style={{ cursor: 'pointer', color: 'blue' }}
                                >
                                    Sign In
                                </a>.
                            </Text>
                        </div>
                    ) : (
                        <>
                            {!isSetPassword && (
                                <>
                                    <div className="justify-between flex-1 flex flex-col min-[700px]:flex-row items-center px-16 xl:gap-72 lg:gap-24 md:gap-12 sm:gap-6">
                                        <div className="self-stretch px-6 inline-flex justify-center items-center gap-2.5">
                                            <Text>Please set a password to enable online access to the portal.</Text>
                                        </div>
                                    </div>
                                    <div className="justify-between flex-1 flex flex-col min-[700px]:flex-row items-center px-16 py-16 xl:gap-72 lg:gap-24 md:gap-12 sm:gap-6">
                                        <div className="self-stretch px-6 inline-flex justify-center items-center gap-2.5">
                                            <Text typography="textSubtitle03Medium">Create a password</Text>
                                        </div>
                                    </div>
                                    <div className='flex flex-col min-[700px]:flex-row max-w-[1200px] w-full'>
                                        <div className="flex flex-col px-6 min-[700px]:px-16 w-full min-[700px]:w-1/2">
                                            <div className="self-stretch px-0 min-[700px]:px-6">
                                                <FormControl>
                                                    <FormInput
                                                        isRequired
                                                        className="w-full"
                                                        placeholder='enter password'
                                                        label='Password'
                                                        name={`password`}
                                                        type={passwordMasked ? "password" : "text"}
                                                        setValue={(newVal) => setPassword(newVal)}
                                                        value={password}
                                                        trailingContent={
                                                            <div
                                                                className="pointer-events-auto"
                                                                style={{ cursor: 'pointer' }}
                                                                onClick={() => setPasswordMasked(!passwordMasked)}
                                                            >
                                                                <LegacyIcon
                                                                    name={passwordMasked ? "visibility" : "visibility-off"}
                                                                    type="filled"
                                                                />
                                                            </div>
                                                        }
                                                    />
                                                </FormControl>
                                            </div>
                                            <div className="self-stretch px-0 min-[700px]:px-6">
                                                <FormControl>
                                                    <FormInput
                                                        isRequired
                                                        className="w-full"
                                                        placeholder='enter password'
                                                        label='Confirm Password'
                                                        name={`confirmPassword`}
                                                        type={confirmPasswordMasked ? "password" : "text"}
                                                        setValue={(newVal) => {
                                                            setConfirmPassword(newVal);
                                                        }}
                                                        value={confirmPassword}
                                                        error={confirmPassword && !passwordsMatch ? "Passwords do not match" : undefined}
                                                        trailingContent={
                                                            <div
                                                                className="pointer-events-auto"
                                                                style={{ cursor: 'pointer' }}
                                                                onClick={() => setConfirmPasswordMasked(!confirmPasswordMasked)}
                                                            >
                                                                <LegacyIcon
                                                                    name={confirmPasswordMasked ? "visibility" : "visibility-off"}
                                                                    type="filled"
                                                                />
                                                            </div>
                                                        }
                                                    />
                                                </FormControl>
                                            </div>

                                        </div>
                                        <div className="flex flex-col px-6 min-[700px]:px-0 w-full min-[700px]:w-1/2 mt-4 min-[700px]:mt-0">
                                            <Text>Your password must:</Text>
                                            <ul className="list-disc list-inside">
                                                <li><Text>Be between 10-64 characters long</Text></li>
                                                <li><Text>Contain one upper-case letter</Text></li>
                                                <li><Text>Contain one lower-case letter</Text></li>
                                                <li><Text>Contain one number or symbol</Text></li>
                                            </ul>
                                        </div>
                                    </div>
                                    <hr
                                        style={{
                                            background: "bg-gray-110",
                                            height: 2,
                                            borderTop: '1px solid #DCE3E8',
                                            margin: '15px 0'
                                        }}
                                    />
                                    <div className="flex px-6 min-[700px]:px-16 w-full">
                                        <div className="self-stretch px-0 min-[700px]:px-6 inline-flex justify-center items-center gap-2.5">
                                            <Checkbox checked={check} onChange={() => setCheck(!check)}>
                                                <Text typography="textBody01Regular">I have read, understand and agree to the terms and the conditions set forth in the <a href={`${import.meta.env.BASE_URL}/customerAgreement.pdf`} target="_blank" className='text-blue-60'>Customer Agreement</a>.</Text>
                                            </Checkbox>
                                        </div>
                                    </div>
                                    <div className="col-span-12 flex justify-end mt-4 px-6 py-4 bg-gray-10">
                                        <Button
                                            className="bg-blue-60 rounded"
                                            size="lg"
                                            type="submit"
                                            onClick={handleSubmit}
                                            disabled={!password || !confirmPassword || !passwordsMatch || !check} 
                                            appearance="neutral"
                                        >
                                            Save Password & Proceed to Login
                                        </Button>
                                    </div>
                                </>
                            )}
                        </>
                    )}
                </>
            )}
            <Footer />
        </>
    );
}
